import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import { ArrowRightSmall, Xmark } from '@peacecover/icon';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import { useReviewablePhotoCardQuery } from './hooks/useReviewablePhotoCardQuery';
import ReviewTradingPhotoCard from './components/ReviewTradingPhotoCard';
import MatchingTradingPhotoCard from './components/MatchingTradingPhotoCard';
import ReviewTradingPhotoCardSkeleton from './components/ReviewTradingPhotoCardSkeleton';
import ConfirmToCompleteTradingPopup from './components/ConfirmToCompleteTradingPopup';

export interface SelectedPhotoCard {
  tradingPhotoCardId: string;
  reviewerId: string;
  reviewableId: string;
  reviewableType: 'TradingPhotoCard' | 'MatchedTradingPhotoCard';
}

const SelectTradePage = () => {
  const handleHistoryBack = useHandleHistoryBack();
  const [isCompletedTradingPopupOpen, setIsCompletedTradingPopupOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedPhotoCard, setSelectedPhotoCard] = useState<SelectedPhotoCard>({
    tradingPhotoCardId: '',
    reviewerId: '',
    reviewableId: '',
    reviewableType: 'TradingPhotoCard'
  });

  const handleNavigateToReviewPage = () => {
    navigate(
      `/reviews/add-review?reviewerId=${selectedPhotoCard.reviewerId}&reviewableId=${selectedPhotoCard.reviewableId}&reviewableType=${selectedPhotoCard.reviewableType}`
    );
  };

  const { data, isLoading } = useReviewablePhotoCardQuery();

  return (
    <div className="min-h-screen bg-pure-white dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))]">
      <div className="flex items-center px-[16px] py-[8px]">
        <button type="button" className="my-[2px]" onClick={handleHistoryBack}>
          <Xmark />
        </button>
      </div>
      <div className="px-[24px] py-[10px] mt-[30px]">
        <p className="text-[26px] font-bold whitespace-pre-line leading-tight">
          {t('SelectTradingPhotoCardReviewPage.selectPhotoCardTitle')}
        </p>
      </div>
      <div className="flex flex-col gap-[16px] mt-[32px] mx-[24px]">
        {isLoading ? (
          <ReviewTradingPhotoCardSkeleton />
        ) : (
          data?.map((photoCard) =>
            photoCard.tradingPhotoCard ? (
              <ReviewTradingPhotoCard
                key={photoCard.reviewableId}
                tradingPhotoCard={photoCard.tradingPhotoCard}
                reviewableId={photoCard.reviewableId}
                setSelectedPhotoCard={setSelectedPhotoCard}
                setIsCompletedTradingPopupOpen={setIsCompletedTradingPopupOpen}
              />
            ) : (
              <MatchingTradingPhotoCard key={photoCard.reviewableId} photoCard={photoCard} />
            )
          )
        )}
      </div>
      <Link to="/feed" className="flex items-center justify-center gap-[4px] mt-[40px] mb-[83px]">
        <p className="text-[13px] text-text-main-lv3 leading-none">
          {t('SelectTradingPhotoCardReviewPage.skip')}
        </p>
        <ArrowRightSmall size={20} className="text-text-main-lv3" />
      </Link>
      {isCompletedTradingPopupOpen && (
        <ConfirmToCompleteTradingPopup
          handleClosePopup={() => setIsCompletedTradingPopupOpen(false)}
          handleNavigateToReviewPage={handleNavigateToReviewPage}
          category={selectedPhotoCard.reviewableType}
        />
      )}
    </div>
  );
};

export default SelectTradePage;
